import { useState, useRef, useCallback, useEffect } from 'react';
import { Calendar, Clock, Check, X, AlertTriangle, Ban, User, Building2, Loader2 } from 'lucide-react';
import { useAuth } from '@/auth';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { VisitTimeEditor } from './VisitTimeEditor';
import { useClientWeeklyVisits, useUpdateVisit, useClientVisitsPaginated, useUpdateVisitTime } from './hooks/useClientDetails';
import type { VisitStatus, Visit } from '@/api/visits';

interface WeeklyVisitsSheetProps {
  clientId: number | undefined;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type TabValue = 'current' | 'past' | 'future';

const STATUS_CONFIG: Record<
  VisitStatus,
  { label: string; icon: typeof Check; variant: 'default' | 'secondary' | 'destructive' | 'outline' }
> = {
  PENDING: { label: 'Ожидает', icon: Clock, variant: 'outline' },
  ACTIVE: { label: 'Активный', icon: Clock, variant: 'default' },
  VISITED: { label: 'Посещён', icon: Check, variant: 'secondary' },
  SKIPPED: { label: 'Пропущен', icon: X, variant: 'destructive' },
  CANCELLED: { label: 'Отменён', icon: Ban, variant: 'outline' },
};

function formatDateTime(dateStr: string | null): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'short',
  });
}

function VisitCard({
  visit,
  currentEmail,
  updateVisit,
  updateVisitTime,
  handleStatusChange,
}: {
  visit: Visit;
  currentEmail: string | null;
  updateVisit: ReturnType<typeof useUpdateVisit>;
  updateVisitTime: ReturnType<typeof useUpdateVisitTime>;
  handleStatusChange: (visitId: string, newStatus: VisitStatus) => void;
}) {
  const [editingTime, setEditingTime] = useState(false);
  const config = STATUS_CONFIG[visit.status as VisitStatus];
  const Icon = config.icon;
  const isUrgent = visit.priority === 'URGENT';
  const wasModified = visit.updatedAt !== visit.createdAt;
  const visitEmployee = visit.employee;
  const showVisitAuthor = visitEmployee && visitEmployee.email !== currentEmail;
  const visitOffice = visit.office;
  const canEditTime = Boolean(visit.canEditTime && visit.startedAt && visit.endedAt);

  return (
    <div
      className="rounded-lg border bg-card p-3"
      data-testid="weekly-visit-row"
      data-visit-id={visit.id}
      data-can-edit-time={canEditTime ? 'true' : 'false'}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant={config.variant} className="text-xs">
              <Icon className="h-3 w-3 mr-1" />
              {config.label}
            </Badge>
            {isUrgent && (
              <Badge variant="urgent" className="text-xs">
                <AlertTriangle className="h-3 w-3 mr-1" />
                Срочно
              </Badge>
            )}
            {visit.deadline && (
              <Badge variant="outline" className="text-xs">
                <Clock className="h-3 w-3 mr-1" />
                до {formatDate(visit.deadline)}
              </Badge>
            )}
          </div>
          {visitOffice && (
            <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
              <Building2 className="h-3 w-3" />
              {visitOffice.name}
            </div>
          )}
          <div className="text-xs text-muted-foreground mt-1 space-y-0.5">
            {visit.plannedDate && (
              <p className="font-medium text-foreground">
                Запланирован на: {formatDate(visit.plannedDate)}
              </p>
            )}
            {showVisitAuthor && (
              <p className="flex items-center gap-1 text-green-600">
                <User className="h-3 w-3" />
                {visitEmployee.firstName} {visitEmployee.lastName}
              </p>
            )}
            <p>Создано: {formatDateTime(visit.createdAt)}</p>
            {wasModified && (
              <p>Изменено: {formatDateTime(visit.updatedAt)}</p>
            )}
            {visit.endedAt && (
              <p>
                {visit.startedAt
                  ? `Время: ${formatDateTime(visit.startedAt)} → ${formatDateTime(visit.endedAt)}`
                  : `Завершено: ${formatDateTime(visit.endedAt)}`}
                {visit.durationMinutes && ` (${visit.durationMinutes} мин)`}
              </p>
            )}
          </div>
          {visit.note && (
            <p className="text-xs text-muted-foreground mt-1 italic">
              {visit.note}
            </p>
          )}
        </div>

        <div className="flex shrink-0 flex-col gap-2">
          {canEditTime && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8"
              onClick={() => setEditingTime((value) => !value)}
              disabled={updateVisitTime.isPending}
            >
              <Clock className="h-4 w-4" />
              Время
            </Button>
          )}
          <Select
            value={visit.status}
            onValueChange={(value) => handleStatusChange(visit.id, value as VisitStatus)}
            disabled={updateVisit.isPending}
          >
            <SelectTrigger className="w-32 bg-background">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-background">
              <SelectItem value="PENDING">Ожидает</SelectItem>
              <SelectItem value="VISITED">Посещён</SelectItem>
              <SelectItem value="SKIPPED">Пропущен</SelectItem>
              <SelectItem value="CANCELLED">Отменён</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {canEditTime && editingTime && (
        <VisitTimeEditor
          className="mt-3"
          startedAt={visit.startedAt}
          endedAt={visit.endedAt}
          label="Время визита"
          expanded={editingTime}
          onExpandedChange={setEditingTime}
          onSave={(payload) => {
            updateVisitTime.mutate(
              { visitId: visit.id, payload },
              { onSuccess: () => setEditingTime(false) },
            );
          }}
          isSaving={updateVisitTime.isPending}
          saveLabel="Сохранить время"
        />
      )}
    </div>
  );
}

function PaginatedVisitsList({
  clientId,
  mode,
  currentEmail,
  updateVisit,
  updateVisitTime,
  handleStatusChange,
}: {
  clientId: number | undefined;
  mode: 'past' | 'future';
  currentEmail: string | null;
  updateVisit: ReturnType<typeof useUpdateVisit>;
  updateVisitTime: ReturnType<typeof useUpdateVisitTime>;
  handleStatusChange: (visitId: string, newStatus: VisitStatus) => void;
}) {
  const {
    data,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useClientVisitsPaginated(clientId, mode, !!clientId);

  const observerRef = useRef<IntersectionObserver | null>(null);
  const sentinelRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (observerRef.current) observerRef.current.disconnect();
      if (!node || !hasNextPage || isFetchingNextPage) return;
      observerRef.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          fetchNextPage();
        }
      });
      observerRef.current.observe(node);
    },
    [hasNextPage, isFetchingNextPage, fetchNextPage],
  );

  const allVisits = data?.pages.flatMap((p) => p.visits) ?? [];
  const emptyMessage = mode === 'past' ? 'Нет прошлых визитов' : 'Нет будущих визитов';

  if (isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-16 w-full" />
      </div>
    );
  }

  if (allVisits.length === 0) {
    return (
      <p className="text-sm text-muted-foreground text-center py-4">
        {emptyMessage}
      </p>
    );
  }

  return (
    <>
      {allVisits.map((visit) => (
        <VisitCard
          key={visit.id}
          visit={visit}
          currentEmail={currentEmail}
          updateVisit={updateVisit}
          updateVisitTime={updateVisitTime}
          handleStatusChange={handleStatusChange}
        />
      ))}
      {hasNextPage && (
        <div ref={sentinelRef} className="flex justify-center py-2">
          {isFetchingNextPage && <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />}
        </div>
      )}
    </>
  );
}

export function WeeklyVisitsSheet({ clientId, open, onOpenChange }: WeeklyVisitsSheetProps) {
  const [activeTab, setActiveTab] = useState<TabValue>('current');
  const { data: visits, isLoading } = useClientWeeklyVisits(open && activeTab === 'current' ? clientId : undefined);
  const updateVisit = useUpdateVisit();
  const updateVisitTime = useUpdateVisitTime();
  const { user } = useAuth();
  const currentEmail = user?.email || null;

  // Reset tab when sheet closes
  useEffect(() => {
    if (!open) setActiveTab('current');
  }, [open]);

  const handleStatusChange = (visitId: string, newStatus: VisitStatus) => {
    updateVisit.mutate({ visitId, updates: { status: newStatus } });
  };

  const tabs: { value: TabValue; label: string }[] = [
    { value: 'current', label: 'Эта неделя' },
    { value: 'past', label: 'Прошлые' },
    { value: 'future', label: 'Будущие' },
  ];

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="max-h-[70vh] overflow-y-auto" hideCloseButton>
        <SheetHeader>
          <div className="flex items-center justify-between">
            <SheetTitle className="text-left flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Визиты клиента
            </SheetTitle>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={(e) => {
                e.stopPropagation();
                onOpenChange(false);
              }}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </SheetHeader>

        <div className="mt-3 flex gap-1 border-b pb-2">
          {tabs.map((tab) => (
            <button
              key={tab.value}
              onClick={() => setActiveTab(tab.value)}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                activeTab === tab.value
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-muted'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="mt-3 space-y-3">
          {activeTab === 'current' && (
            <>
              {isLoading ? (
                <div className="space-y-3">
                  <Skeleton className="h-16 w-full" />
                  <Skeleton className="h-16 w-full" />
                </div>
              ) : visits && visits.length > 0 ? (
                visits.map((visit) => (
                  <VisitCard
                    key={visit.id}
                    visit={visit}
                    currentEmail={currentEmail}
                    updateVisit={updateVisit}
                    updateVisitTime={updateVisitTime}
                    handleStatusChange={handleStatusChange}
                  />
                ))
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">
                  Нет визитов на этой неделе
                </p>
              )}
            </>
          )}

          {(activeTab === 'past' || activeTab === 'future') && (
            <PaginatedVisitsList
              clientId={clientId}
              mode={activeTab}
              currentEmail={currentEmail}
              updateVisit={updateVisit}
              updateVisitTime={updateVisitTime}
              handleStatusChange={handleStatusChange}
            />
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
